import { inject, DOCUMENT, Service } from '@angular/core';
import type { MetaTagsDto } from '@api/models';
import { Meta, Title } from '@angular/platform-browser';
import { Language } from '@api/models';
import { UrlService } from './url.service';
import { LanguageService } from './language/language.service';

export interface IOgTags {
  type: 'product' | 'products' | 'article';
  title?: string;
  image?: string;
  description?: string;
  url: string;
  site_name?: string;
}

@Service()
export class HeadService {
  private document = inject<Document>(DOCUMENT);
  private title = inject(Title);
  private meta = inject(Meta);
  private urlService = inject(UrlService);
  private languageService = inject(LanguageService);


  setMeta(metaTags: Partial<MetaTagsDto>, ogTags?: IOgTags) {
    // this.title.setTitle('STG | ' + metaTags.title || '');
    this.title.setTitle(metaTags.title || '');
    this.meta.updateTag({ name: 'description', content: metaTags.description });
    this.meta.updateTag({ name: 'keywords', content: metaTags.keywords || metaTags.title });

    if (ogTags) {
      this.meta.updateTag({ property: 'og:site_name', content: 'Klondike' });
      this.meta.updateTag({ property: 'og:type', content: ogTags.type });
      this.meta.updateTag({ property: 'og:title', content: ogTags.title || metaTags.title });
      this.meta.updateTag({ property: 'og:description', content: ogTags.description || metaTags.description });
      this.meta.updateTag({ property: 'og:url', content: ogTags.url });

      if (ogTags.image) {
        this.meta.updateTag({ property: 'og:image', content: ogTags.image });
      }
    }

    this.setHreflangLinks();
    this.setCanonicalLink();
  }

  setHreflangLinks(): void {
    const ensureLink = (langForUrl: Language, hreflang: string) => {
      const link = this.document.head.querySelector<HTMLLinkElement>(`link[rel='alternate'][hreflang='${hreflang}']`)
        || this.document.createElement('link');

      link.rel = 'alternate';
      link.hreflang = hreflang;
      link.href = this.urlService.buildCurrentUrlWithLang(langForUrl, false);

      const firstHeadChild = this.document.head.firstChild;
      this.document.head.insertBefore(link, firstHeadChild);
    };

    ensureLink(Language.RU, Language.RU);
    ensureLink(Language.UK, Language.UK);
    ensureLink(Language.UK, 'x-default');
  }

  setCanonicalLink(): void {
    const canonicalLink = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      || this.document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = this.urlService.buildCurrentUrlWithLang(this.languageService.getCurrentLang(), false);
    const firstHeadChild = this.document.head.firstChild;
    this.document.head.insertBefore(canonicalLink, firstHeadChild);
  }
}
